import { useSelector } from 'react-redux';
import { useCallback } from 'react';

import { FinanceOffer, FinanceResult } from 'state/financeOffers';
import useUnifiedCheckout, {
  UnifiedCheckoutRequest,
  interpretDenialStatusCodes,
  UnifiedCheckoutError,
} from 'hooks/requests/unifiedCheckout';
import useTranslate from 'hooks/useTranslate';
import { useSnackbar } from 'notistack';
import useNavigationHelpers from 'hooks/useNavigationHelpers';
import { selectCreditApplication } from 'state/creditApplication';
import catchOnly from 'utils/catchOnly';
import { NonClientError } from 'hooks/requests/fetchUtils';
import { useTheme } from 'emotion-theming';
import { Theme } from 'features/Theme';

type CheckoutHook = (
  financeResult: FinanceResult,
  selectedOffer: FinanceOffer,
) => (request: {
  disclosureChecksum: string;
  braintreeNonce?: string;
  braintreeCardType?: 'DEBIT' | 'CREDIT';
}) => Promise<void>;

const useCheckout: CheckoutHook = (
  financeResult: FinanceResult,
  selectedOffer: FinanceOffer,
) => {
  const [unifiedCheckoutRequest] = useUnifiedCheckout();
  const t = useTranslate();

  const { enqueueSnackbar } = useSnackbar();
  const { push } = useNavigationHelpers();

  const {
    firstName,
    lastName,
    emailAddress,
    mobilePhone,
    order,
    shippingAddress,
    intendedPurchaseAmount,
  } = useSelector(selectCreditApplication);

  const theme = useTheme<Theme>();

  return useCallback(
    async ({ disclosureChecksum, braintreeCardType, braintreeNonce }) => {
      const type = selectedOffer.paymentProductType;
      const paymentAgreementId = selectedOffer.paymentAgreementId;
      const unifiedCheckoutValues: UnifiedCheckoutRequest = {
        firstName,
        lastName,
        emailAddress,
        mobilePhone,
        applicationId: financeResult.id,
        paymentAgreementId: paymentAgreementId,
        braintreeNonce,
        braintreeCardType,
        intendedPurchaseAmount,
        order,
        shippingAddress,
        paymentProductType: type,
        disclosureChecksum,
      };

      try {
        const checkoutResponse = await unifiedCheckoutRequest(
          unifiedCheckoutValues,
        );
        if (checkoutResponse.status !== 'CHECKOUT_COMPLETED') {
          switch (interpretDenialStatusCodes(checkoutResponse.statusCodes)) {
            case 'fraud':
              push('/unified/checkout/place-order/fraud-alert');
              break;
            case 'fraudOfac':
              push('/unified/checkout/place-order/fraud-alert-ofac');
              break;
            case 'splitpayAuthFailureRetry':
              enqueueSnackbar(
                t('unified.placeOrder.error.genericSnackbarError'),
                {
                  variant: 'error',
                  persist: true,
                },
              );
              break;
            case 'pending':
              push('/unified/checkout/place-order/pending');
              break;
            default:
              // This is a technical glitch scenario
              enqueueSnackbar(
                t('unified.placeOrder.error.genericSnackbarError'),
                {
                  variant: 'error',
                  persist: true,
                },
              );
              break;
          }
        } else {
          if (!theme.embedded) {
            push('/unified/checkout/success');
          }
        }
      } catch (e) {
        if (catchOnly(e, NonClientError, UnifiedCheckoutError)) {
          enqueueSnackbar(t('unified.placeOrder.error.genericSnackbarError'), {
            variant: 'error',
            persist: true,
          });
        }
      }
    },
    [
      emailAddress,
      enqueueSnackbar,
      financeResult.id,
      firstName,
      intendedPurchaseAmount,
      lastName,
      mobilePhone,
      order,
      push,
      selectedOffer.paymentAgreementId,
      selectedOffer.paymentProductType,
      shippingAddress,
      t,
      theme.embedded,
      unifiedCheckoutRequest,
    ],
  );
};

export default useCheckout;
